/**
 * Config objects for the 3 config-driven shared features × 2 products.
 * `basePath` replaces `apiBaseUrl` from the legacy configs — the axios
 * client (`serviceApiV3`) owns the host; callers concatenate basePath + endpoint.
 * `queryKeyPrefix` values match the legacy keys so cached data remains compatible.
 *
 * Path construction: use `mediaBase(serviceId, kind)` to build service-scoped
 * paths (`/services/${serviceId}/${kind}`), where `kind` is `'vod'` or
 * `'streams'`. The raw `basePath` on config objects carries the
 * `/vod` | `/streams` value for reference but is no longer used
 * directly in requests — callers go through `mediaBase`.
 */

// ============================================
// Path helper
// ============================================

/** Kind values matching the two live/vod product paths. */
export type MediaPathKind = 'vod' | 'streams';

/**
 * Build the service-scoped base path for a live or vod product.
 * Result: `/services/${serviceId}/${kind}` (e.g. `/services/abc123/vod`)
 * Both `vod` (VOD) and `streams` (Live) paths go through this helper.
 */
export function mediaBase(serviceId: string, kind: MediaPathKind): string {
  return `/services/${serviceId}/${kind}`;
}

// ============================================
// Types (mirrors Shared config interfaces)
// ============================================

export interface FileTransferLabels {
  pageTitle: string;
  pageDescription: string;
  createButtonText: string;
  createDialogTitle: string;
  createDialogDescription: string;
  editDialogTitle: string;
  editDialogDescription: string;
  deleteDialogTitle: string;
  deleteDialogDescription: (name: string) => string;
}

export interface FileTransferConfig {
  /** `'vod'` or `'streams'` — the kind segment used with `mediaBase()`. */
  kind: MediaPathKind;
  /** @deprecated Use `kind` with `mediaBase()`. Kept for label display only. */
  basePath: '/vod' | '/streams';
  queryKeyPrefix: string;
  labels: FileTransferLabels;
}

export interface CDNLabels {
  pageTitle: string;
  pageDescription: string;
  createButtonText: string;
  addDialogTitle: string;
  addDialogDescription: string;
  updateDialogTitle: string;
  updateDialogDescription: (name: string) => string;
  detailDialogTitle: string;
  deleteDialogTitle: string;
  deleteDialogDescription: (name: string) => string;
}

export interface CDNConfig {
  /** `'vod'` or `'streams'` — the kind segment used with `mediaBase()`. */
  kind: MediaPathKind;
  /** @deprecated Use `kind` with `mediaBase()`. Kept for label display only. */
  basePath: '/vod' | '/streams';
  queryKeyPrefix: string;
  labels: CDNLabels;
}

export interface VerimatrixDRMLabels {
  pageTitle: string;
  pageDescription: string;
  createButtonText: string;
  addDialogTitle: string;
  addDialogDescription: string;
  updateDialogTitle: string;
  updateDialogDescription: string;
  detailDialogTitle: string;
  deleteDialogTitle: string;
  deleteDialogDescription: (name: string) => string;
}

export interface VerimatrixDRMConfig {
  /** `'vod'` or `'streams'` — the kind segment used with `mediaBase()`. */
  kind: MediaPathKind;
  /** @deprecated Use `kind` with `mediaBase()`. Kept for label display only. */
  basePath: '/vod' | '/streams';
  queryKeyPrefix: string;
  labels: VerimatrixDRMLabels;
}

// ============================================
// Vod (Cloud Encoding) configs
// ============================================

export const vodCDNConfig: CDNConfig = {
  kind: 'vod',
  basePath: '/vod',
  queryKeyPrefix: 'vod',
  labels: {
    pageTitle: 'CDN Endpoints',
    pageDescription: 'Manage external CDN endpoints for distribution.',
    createButtonText: 'Create CDN',
    addDialogTitle: 'Add CDN Endpoint',
    addDialogDescription: 'Configure a new Content Delivery Network endpoint.',
    updateDialogTitle: 'Update CDN Endpoint',
    updateDialogDescription: (name) => `Update CDN "${name}".`,
    detailDialogTitle: 'CDN Details',
    deleteDialogTitle: 'Delete CDN Endpoint',
    deleteDialogDescription: (name) =>
      `This will permanently delete CDN "${name}". This action cannot be undone.`,
  },
};

export const vodFileTransferConfig: FileTransferConfig = {
  kind: 'vod',
  basePath: '/vod',
  queryKeyPrefix: 'vod',
  labels: {
    pageTitle: 'Output Storage',
    pageDescription:
      'Manage output storage configurations for file transfers and distributions.',
    createButtonText: 'Add Storage',
    createDialogTitle: 'Create Output Storage',
    createDialogDescription:
      'Configure destination for media delivery, transcoding output or archive exports.',
    editDialogTitle: 'Edit Output Storage',
    editDialogDescription:
      'Update the destination configuration. Credentials must be re-entered.',
    deleteDialogTitle: 'Delete Storage',
    deleteDialogDescription: (name) =>
      `This will permanently delete storage "${name}". This action cannot be undone.`,
  },
};

export const vodVerimatrixConfig: VerimatrixDRMConfig = {
  kind: 'vod',
  basePath: '/vod',
  queryKeyPrefix: 'vod',
  labels: {
    pageTitle: 'Verimatrix DRM',
    pageDescription: 'Manage external Verimatrix DRM endpoints.',
    createButtonText: 'Create DRM',
    addDialogTitle: 'Add Verimatrix DRM',
    addDialogDescription: 'Configure a new Verimatrix DRM endpoint.',
    updateDialogTitle: 'Edit Verimatrix DRM',
    updateDialogDescription: 'Update the configuration for this Verimatrix DRM endpoint.',
    detailDialogTitle: 'Verimatrix DRM Details',
    deleteDialogTitle: 'Delete Verimatrix DRM',
    deleteDialogDescription: (name) =>
      `This will permanently delete Verimatrix DRM "${name}". This action cannot be undone.`,
  },
};

// ============================================
// Live (Live Transcoding) configs
// ============================================

export const liveCDNConfig: CDNConfig = {
  kind: 'streams',
  basePath: '/streams',
  queryKeyPrefix: 'livestream',
  labels: {
    pageTitle: 'CDN Endpoints',
    pageDescription: 'Manage CDN endpoints for live stream delivery.',
    createButtonText: 'Create CDN',
    addDialogTitle: 'Add CDN Endpoint',
    addDialogDescription: 'Configure a new Content Delivery Network endpoint.',
    updateDialogTitle: 'Update CDN Endpoint',
    updateDialogDescription: (name) => `Update CDN "${name}".`,
    detailDialogTitle: 'CDN Details',
    deleteDialogTitle: 'Delete CDN Endpoint',
    deleteDialogDescription: (name) =>
      `This will permanently delete CDN "${name}". This action cannot be undone.`,
  },
};

export const liveFileTransferConfig: FileTransferConfig = {
  kind: 'streams',
  basePath: '/streams',
  queryKeyPrefix: 'livestream',
  labels: {
    pageTitle: 'File Transfer',
    pageDescription:
      'Manage storage configurations for live stream recordings, exports, and delivery.',
    createButtonText: 'Add Storage',
    createDialogTitle: 'Create File Transfer Storage',
    createDialogDescription:
      'Configure destination for live stream output or archive exports.',
    editDialogTitle: 'Edit File Transfer Storage',
    editDialogDescription:
      'Update the destination configuration. Credentials must be re-entered.',
    deleteDialogTitle: 'Delete Storage',
    deleteDialogDescription: (name) =>
      `This will permanently delete storage "${name}". This action cannot be undone.`,
  },
};

export const liveVerimatrixConfig: VerimatrixDRMConfig = {
  kind: 'streams',
  basePath: '/streams',
  queryKeyPrefix: 'livestream',
  labels: {
    pageTitle: 'Verimatrix DRM',
    pageDescription: 'Manage Verimatrix DRM endpoints for live streams.',
    createButtonText: 'Create DRM',
    addDialogTitle: 'Add Verimatrix DRM',
    addDialogDescription: 'Configure a new Verimatrix DRM endpoint.',
    updateDialogTitle: 'Edit Verimatrix DRM',
    updateDialogDescription: 'Update the configuration for this Verimatrix DRM endpoint.',
    detailDialogTitle: 'Verimatrix DRM Details',
    deleteDialogTitle: 'Delete Verimatrix DRM',
    deleteDialogDescription: (name) =>
      `This will permanently delete Verimatrix DRM "${name}". This action cannot be undone.`,
  },
};
